import { QueryOptions, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { blobToBase64 } from '../utils/blobBase64Conversions';
import { appAPI, publicAPI } from '../utils/api';

export type UseAvatarArgs = {
    avatarAPIUrl: string | null;
    /**
     * Avatars are usually cached by url.  That can be too unique for things like note comments where
     * the same user ID will be used for multiple comments.  This allows you to specify a cache key
     */
    avatarCacheKey?: string;
    usePublicAPI?: boolean;
};

export type AvatarData = {
    name: string;
    base64: string;
    imageFetchUrl: string | null;
};

export type UseAvatarReturnValue = {
    avatar: AvatarData | null;
    isLoadingAvatar: boolean;
    avatarAPIUrl: string | null;
};

export const useClearInMemoryAvatarCache = () => {
    const queryClient = useQueryClient();

    return (avatarAPIUrl: string) => queryClient.refetchQueries({ queryKey: [avatarAPIUrl] });
};

export type GetAvatarDownloadUrlResponse = {
    avatarUrl: string;
    name: string;
} | null;

async function fetchAvatar(avatarAPIUrl: string, usePublicAPI: boolean): Promise<AvatarData | null> {
    const api = usePublicAPI ? publicAPI : appAPI;
    try {
        let imageFetchUrl: string | null = null;
        let response: Response | null = null;
        let name = '';
        if (avatarAPIUrl.includes('/one-up-health/image')) {
            const downloadURLResult = await api.get<string>(avatarAPIUrl, {
                textResponse: true,
            });
            imageFetchUrl = downloadURLResult;
            return {
                name,
                base64: downloadURLResult,
                imageFetchUrl: avatarAPIUrl,
            };
        } else if (avatarAPIUrl.includes('integration/branding/images/')) {
            const downloadURLResult = await api.get<GetAvatarDownloadUrlResponse>(avatarAPIUrl);
            if (!downloadURLResult) return null;

            // download file from stripe and return base64
            const response = await fetch(downloadURLResult.avatarUrl);
            if (response.status !== 200) return null;

            const blob = await response.blob();
            const base64 = await blobToBase64(blob);
            return {
                name,
                base64,
                imageFetchUrl: avatarAPIUrl,
            };
        } else if (avatarAPIUrl.includes('http')) {
            imageFetchUrl = avatarAPIUrl;
            response = await fetch(avatarAPIUrl);
        } else {
            const downloadURLResult = (await api.get(avatarAPIUrl)) as GetAvatarDownloadUrlResponse;
            imageFetchUrl = downloadURLResult?.avatarUrl || null;

            if (!downloadURLResult?.avatarUrl) return null;

            name = downloadURLResult.name;
            response = await fetch(downloadURLResult.avatarUrl);
        }

        if (response.status !== 200) return null;

        const blob = await response.blob();
        const base64 = await blobToBase64(blob);

        return {
            name,
            base64,
            imageFetchUrl: imageFetchUrl,
        };
    } catch (e) {
        console.error(e);
        return null;
    }
}

export const useAvatar = ({ avatarAPIUrl, avatarCacheKey, usePublicAPI }: UseAvatarArgs): UseAvatarReturnValue => {
    const avatarQuery = useQuery({
        queryKey: avatarCacheKey ? [avatarCacheKey] : [avatarAPIUrl],
        queryFn: async () => fetchAvatar(avatarAPIUrl || '', usePublicAPI || false),
        enabled: !!avatarAPIUrl,
    });

    return {
        // @ts-expect-error TS2322
        avatar: avatarQuery.data,
        isLoadingAvatar: avatarQuery.isLoading,
        avatarAPIUrl,
    };
};

export const useAvatars = ({ avatarAPIUrls }: { avatarAPIUrls: string[] }): UseAvatarReturnValue[] => {
    const queries = avatarAPIUrls.map<QueryOptions<AvatarData | null>>((avatarAPIUrl) => ({
        queryKey: [avatarAPIUrl],
        queryFn: () => fetchAvatar(avatarAPIUrl, false),
    }));
    const queryResults = useQueries({ queries: queries as any });
    return queryResults.map((queryResult, index) => ({
        avatar: queryResult.data,
        isLoadingAvatar: queryResult.isLoading,
        avatarAPIUrl: avatarAPIUrls[index],
    })) as any;
};

export type MaybeAvatarArgs = {
    avatarAPIUrl: string | null;
    usePublicAPI?: boolean;
};

export type MaybeAvatarReturnValue = UseAvatarReturnValue | null;

/**
 * Same as useAvatar, but can be called without an avatarAPIUrl.  If no avatarAPIUrl is provided, the hook will return null.
 */
export const useMaybeAvatar = ({ avatarAPIUrl, usePublicAPI }: MaybeAvatarArgs): MaybeAvatarReturnValue => {
    const result = useAvatar({ avatarAPIUrl: avatarAPIUrl || '', usePublicAPI: usePublicAPI || false });

    return avatarAPIUrl ? result : null;
};
